import React, { useEffect, useState } from 'react';
import {
  Box,
  GDSStyleObject,
  Modal,
  HeadingText,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { getAccessToken } from '../helper/helpers';
import { disclaimer } from '../disclaimer.config';
import * as CONSTANTS from '../utils/constant';
import { ErrorModal } from './errorModal';

export function Disclaimer({ onClose }: { onClose?: () => void }) {
  const config = disclaimer.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const disclaimerEndPoint = `${HIGHMARK_API_URL}/digital/v3/members/benefits/disclaimers`;
  const [disclaimerData, setDisclaimerData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const currentUbk: any = useCurrentRegionUbkHeader() || '';
  const ubkValue = currentUbk?.headers?.Brandubk || null;

  const getDisclaimerContent = () => {
    setIsLoading(true);
    getAccessToken(disclaimerEndPoint, ubkValue).then((result: any) => {
      if (result.disclaimer) {
        setDisclaimerData(result.disclaimer);
        setIsLoading(false);
      } else if (result.errMsg || !result.disclaimer) {
        setIsLoading(false);
        setModalOpen(false);
        setErrMsg(true);
      } else {
        setIsLoading(false);
      }
    });
  };

  const closeErrorModal = () => {
    setErrMsg(false);
  };

  useEffect(() => {
    getDisclaimerContent();
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      screen_name: CONSTANTS.DISCLAIMER,
      product_area: CONSTANTS.DISCLAIMER,
      path: window.location.pathname,
      event_author: CONSTANTS.ENGEN,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      setErrMsg(false);
      setIsLoading(false);
      setModalOpen(false);
    },
    [],
  );

  return (
    <Box>
      <Modal.Root
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) {
            onClose?.();
          }
        }}
      >
        {errMsg ? (
          <ErrorModal onClose={closeErrorModal} />
        ) : (
          <Modal.Content
            padding={{
              '@initial': '40px 0 80px',
              '@laptop': '40px 0 80px',
              '@tablet': '0  0 35px ',
              '@mobile': '0  0 35px',
              '@mobileLandscape': '0  0 35px',
            }}
            css={
              {
                borderRadius: '12px',
                background: '#fff',
                height: '100px',
                maxHeight: '90vh',
                '& .GDS-modal-close-button': {
                  color: '#66717a',
                },
                '& .GDS-modal': {
                  '@mobile': {
                    overflowY: 'hidden',
                  },
                },
              } as GDSStyleObject
            }
            width={{
              '@initial': '65%',
              '@laptop': '80%',
              '@mobile': '100%',
              '@tablet': '80%',
              '@mobileLandscape': '80%',
            }}
          >
            <Box
              css={{
                height: '600px',
                maxHeight: '70vh',
                overflowY: 'auto',
                '@mobile': {
                  width: '100%',
                  height: '100%',
                  overflow: 'auto !important',
                  padding: '0 18px',
                  minHeight: '86vh',
                  maxHeight: 'auto',
                },
                '@mobileLandscape': {
                  padding: '0 18px',
                },
              }}
              data-testid="modalContent"
            >
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <Modal.Title>
                    <HeadingText
                      level="4"
                      size={{
                        '@initial': 'xxl',
                        '@mobile': 'xxl',
                      }}
                      css={{
                        fontSize: '24px',
                        fontFamily: `Roboto, BlinkMacSystemFont, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif`,
                        'letter-spacing': '-0.2px',
                        lineHeight: '24px',
                        fontWeight: '700',
                        '@desktop': {
                          fontFamily: `Poppins, BlinkMacSystemFont, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
                          'letter-spacing': '-0.5px',
                          lineHeight: '56px',
                          fontSize: '36px',
                        },
                        '@mobile': { fontSize: '17px' },
                        maxWidth: '600px',
                        margin: 'auto',
                      }}
                    >
                      {CONSTANTS.DISCLAIMER}
                    </HeadingText>
                  </Modal.Title>
                  <Modal.Description>
                    <ParagraphText
                      css={
                        {
                          fontSize: '$bodyOne',
                          lineHeight: '32px',
                          color: 'rgba(0, 0, 0, 1 )',
                          fontWeight: '400',
                          maxWidth: '600px',
                          margin: '$one auto 0',
                          whiteSpace: 'pre-line',
                          fontFamily: `Roboto, BlinkMacSystemFont, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif`,
                          '@mobile': {
                            lineHeight: '24px',
                            margin: '10px auto 0',
                          },
                        } as GDSStyleObject
                      }
                      data-testid="test-description"
                    >
                      {disclaimerData}
                    </ParagraphText>
                  </Modal.Description>
                </>
              )}
            </Box>
          </Modal.Content>
        )}
      </Modal.Root>
    </Box>
  );
}
